import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
} from '@mui/material';
import {ExpandMore, Close} from '@mui/icons-material';
import useRootHook from '../../hooks/useRoot';
import stats from '../../models/stats';
import countries from '../../helpers/countries';

export default function PaymethodSuccessDeclineTable({filters}) {
    const {setShowLoader} = useRootHook();
    const [data, setData] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [filterCountry, setFilterCountry] = useState('');
    const [filterStripeTag, setFilterStripeTag] = useState('');
    const [availableStripeTags, setAvailableStripeTags] = useState([]);
    const [countryStats, setCountryStats] = useState(null);

    const reqKey = useMemo(() => JSON.stringify({
        type: 'paymethodSuccessDecline',
        from: filters.from,
        to: filters.to,
        paymethod_id: filters.paymethod_id || null,
        project_id: filters.project_id || null,
        country: filterCountry || null,
        stripe_tag: filterStripeTag || null,
    }), [filters.from, filters.to, filters.paymethod_id, filters.project_id, filterCountry, filterStripeTag]);

    // Load main table data
    useEffect(() => {
        setShowLoader(true);
        const queryFilters = {...filters};
        if (filterCountry) queryFilters.country = filterCountry;
        if (filterStripeTag) queryFilters.stripe_tag = filterStripeTag;
        
        Promise.all([
            stats.getStats({type: 'paymethodSuccessDecline', ...queryFilters}),
            stats.getStats({type: 'countrySuccessDecline', ...filters}) // Load country stats separately (without country/stripe_tag filters)
        ])
            .then(([paymethodResp, countryResp]) => {
                setShowLoader(false);
                setData(paymethodResp || {items: []});
                setCountryStats(countryResp || {items: []});
                
                // Extract unique stripe tags from data
                const tags = new Set();
                (paymethodResp?.items || []).forEach(item => {
                    if (item['paymethod.stripe_tag'] && item['paymethod.paysystem.key'] === 'stripe') {
                        tags.add(item['paymethod.stripe_tag']);
                    }
                });
                setAvailableStripeTags(Array.from(tags).sort());
            })
            .catch(() => setShowLoader(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reqKey]);

    const handleRowClick = (row) => {
        const stripeTag = row['paymethod.stripe_tag'];
        const isStripe = row['paymethod.paysystem.key'] === 'stripe';
        
        console.log('[PaymethodSuccessDeclineTable] handleRowClick:', {
            stripeTag,
            isStripe,
            row: row
        });
        
        if (!isStripe || !stripeTag) {
            console.log('[PaymethodSuccessDeclineTable] Cannot open details: not Stripe or no stripe_tag');
            return;
        }
        
        setSelectedRow(row);
        setShowLoader(true);
        
        // Load country stats for this Stripe tag
        const queryParams = {
            type: 'paymethodCountryStats',
            stripe_tag: stripeTag,
            from: filters.from,
            to: filters.to,
            paymethod_id: filters.paymethod_id || null,
            project_id: filters.project_id || null,
        };
        
        console.log('[PaymethodSuccessDeclineTable] Loading country stats with params:', queryParams);
        
        stats.getStats(queryParams)
        .then((resp) => {
            console.log('[PaymethodSuccessDeclineTable] Received response:', resp);
            setShowLoader(false);
            setDetailData(resp || {items: []});
            setDetailDialogOpen(true);
        })
        .catch((err) => {
            console.error('[PaymethodSuccessDeclineTable] Error loading country stats:', err);
            setShowLoader(false);
            setDetailDialogOpen(true);
            setDetailData({items: []});
        });
    };

    const handleCountryClick = (country) => {
        // Update the dialog to show Stripe accounts for this country
        setSelectedRow({country});
        setShowLoader(true);
        
        // Load Stripe tag stats for this country
        stats.getStats({
            type: 'countryPaymethodStats',
            country: country,
            from: filters.from,
            to: filters.to,
            paymethod_id: filters.paymethod_id || null,
            project_id: filters.project_id || null,
        })
        .then((resp) => {
            setShowLoader(false);
            setDetailData(resp || {items: []});
            // Dialog is already open, just update the content
        })
        .catch(() => {
            setShowLoader(false);
            setDetailData({items: []});
        });
    };

    const handleCloseDialog = () => {
        setDetailDialogOpen(false);
        setDetailData(null);
        setSelectedRow(null);
    };

    const rows = data?.items || [];

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                    Successful vs declined by payment method
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Country</InputLabel>
                        <Select
                            value={filterCountry}
                            label="Country"
                            onChange={(e) => setFilterCountry(e.target.value)}
                        >
                            <MenuItem value="">All countries</MenuItem>
                            {countries.map((country) => (
                                <MenuItem key={country.code} value={country.code}>
                                    {country.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Stripe Tag</InputLabel>
                        <Select
                            value={filterStripeTag}
                            label="Stripe Tag"
                            onChange={(e) => setFilterStripeTag(e.target.value)}
                        >
                            <MenuItem value="">All Stripe tags</MenuItem>
                            {availableStripeTags.map((tag) => (
                                <MenuItem key={tag} value={tag}>
                                    {tag}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(filterCountry || filterStripeTag) && (
                        <Button 
                            size="small" 
                            onClick={() => {
                                setFilterCountry('');
                                setFilterStripeTag('');
                            }}
                        >
                            Clear Filters
                        </Button>
                    )}
                </Box>
            </Box>
            <Table size="small" sx={{ width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 300 }}>Payment method</TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>Successful</TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>Declined</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => {
                        const stripeTag = r['paymethod.stripe_tag'];
                        const isStripe = r['paymethod.paysystem.key'] === 'stripe';
                        const label = r['paymethod.name']
                            ? `${r['paymethod.name']}${r['paymethod.paysystem.key'] ? ` (${String(r['paymethod.paysystem.key']).toUpperCase()})` : ''}${stripeTag ? ` · ${stripeTag}` : ''}`
                            : `#${r.paymethod_id}`;
                        // Only clickable if Stripe, has stripe_tag (not empty), and not filtered by country
                        const isClickable = isStripe && stripeTag && stripeTag.trim() !== '' && !filterCountry;

                        return (
                            <TableRow 
                                key={`paymethod-${r.paymethod_id}`}
                                onClick={() => isClickable && handleRowClick(r)}
                                sx={{
                                    cursor: isClickable ? 'pointer' : 'default',
                                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : {}
                                }}
                            >
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {label}
                                        {isClickable && <ExpandMore fontSize="small" color="action" />}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">{r.completed_count || 0}</TableCell>
                                <TableCell align="right">{r.declined_count || 0}</TableCell>
                            </TableRow>
                        );
                    })}
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={3}>
                                <Typography variant="body2" color="text.secondary">
                                    No data for selected range.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {/* Countries statistics table */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 2 }}>
                    Successful vs declined by country
                </Typography>
                <Table size="small" sx={{ width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 200 }}>Country</TableCell>
                            <TableCell align="right" sx={{ minWidth: 120 }}>Successful</TableCell>
                            <TableCell align="right" sx={{ minWidth: 120 }}>Declined</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(countryStats?.items || []).map((item) => {
                            const countryName = countries.find(c => c.code === item.country)?.name || item.country || 'Unknown';
                            return (
                                <TableRow 
                                    key={`country-${item.country}`}
                                    onClick={() => handleCountryClick(item.country)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {countryName}
                                            <ExpandMore fontSize="small" color="action" />
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">{item.completed_count || 0}</TableCell>
                                    <TableCell align="right">{item.declined_count || 0}</TableCell>
                                </TableRow>
                            );
                        })}
                        {(!countryStats?.items || countryStats.items.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        No data for selected range.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Dialog 
                open={detailDialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {selectedRow?.['paymethod.stripe_tag'] 
                                ? `Countries for Stripe: ${selectedRow['paymethod.stripe_tag']}`
                                : selectedRow?.country
                                ? `Stripe accounts for country: ${countries.find(c => c.code === selectedRow.country)?.name || selectedRow.country}`
                                : 'Details'}
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Table size="small" sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 200 }}>
                                    {selectedRow?.['paymethod.stripe_tag'] ? 'Country' : 'Stripe Account'}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 120 }}>Successful</TableCell>
                                <TableCell align="right" sx={{ minWidth: 120 }}>Declined</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(detailData?.items || []).map((item, index) => {
                                const label = selectedRow?.['paymethod.stripe_tag'] 
                                    ? (countries.find(c => c.code === item.country)?.name || item.country || 'Unknown')
                                    : (item['paymethod.name'] 
                                        ? `${item['paymethod.name']}${item['paymethod.stripe_tag'] ? ` · ${item['paymethod.stripe_tag']}` : ''}`
                                        : `#${item.paymethod_id}`);
                                
                                // If showing countries (for a Stripe tag), make countries clickable
                                // If showing Stripe accounts (for a country), they are not clickable (limit depth)
                                const isClickable = selectedRow?.['paymethod.stripe_tag'] && item.country;
                                
                                return (
                                    <TableRow 
                                        key={`detail-${index}`}
                                        onClick={() => {
                                            if (isClickable) {
                                                handleCountryClick(item.country);
                                            }
                                        }}
                                        sx={{
                                            cursor: isClickable ? 'pointer' : 'default',
                                            '&:hover': isClickable ? { bgcolor: 'action.hover' } : {}
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {label}
                                                {isClickable && <ExpandMore fontSize="small" color="action" />}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{item.completed_count || 0}</TableCell>
                                        <TableCell align="right">{item.declined_count || 0}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {(!detailData?.items || detailData.items.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            No data available.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

